import React, { useState, useEffect, useRef } from 'react';
import { FiMail, FiPhone, FiMapPin, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const Contact = () => {
  const contactRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ 
    submitting: false, 
    success: false, 
    error: false 
  });
  const [formErrors, setFormErrors] = useState({});
  
  useEffect(() => {
    const currentRef = contactRef.current;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fadeInUp');
        }
      });
    }, { threshold: 0.1 });
    
    if (currentRef) {
      observer.observe(currentRef);
    }
    
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message should be at least 10 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setFormStatus({ submitting: true, success: false, error: false });
    
    try {
      const response = await fetch('https://formspree.io/f/xovwpgpa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _replyto: formData.email,
          _subject: `New message from ${formData.name}: ${formData.subject}`
        })
      });
      
      if (response.ok) {
        setFormStatus({ submitting: false, success: true, error: false });
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        setTimeout(() => {
          setFormStatus(prev => ({ ...prev, success: false }));
        }, 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus({ submitting: false, success: false, error: true });
      
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, error: false }));
      }, 5000);
    }
  };

  return (
    <section id="contact" ref={contactRef} className="py-20 bg-white/95 dark:bg-gray-900/95 opacity-1 transform translate-y-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-black dark:text-white">Get In Touch</h2>
          <p className="mt-4 text-xl text-black dark:text-gray-400 max-w-3xl mx-auto font-bold">
            Have a project in mind or want to discuss opportunities? I'd love to hear from you!
          </p>
        </div>
        
        {formStatus.success && (
          <div className="mb-8 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800 flex items-center">
            <FiCheckCircle className="text-green-500 dark:text-green-400 text-xl mr-3" />
            <span className="text-green-700 dark:text-green-300 font-bold">
              Message sent successfully! I'll get back to you soon.
            </span>
          </div>
        )}
        
        {formStatus.error && (
          <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800 flex items-center">
            <FiAlertCircle className="text-red-500 dark:text-red-400 text-xl mr-3" />
            <span className="text-red-700 dark:text-red-300 font-bold">
              Failed to send message. Please try again or contact me directly at sagarathinarapu152@gmail.com.
            </span>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold mb-6 text-indigo-600 dark:text-indigo-400">Contact Information</h3>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-indigo-100 dark:bg-indigo-900/50 p-3 rounded-lg mr-4">
                  <FiMail className="text-indigo-600 dark:text-indigo-400 text-xl" />
                </div>
                <div>
                  <h4 className="font-bold text-black dark:text-white">Email</h4>
                  <a href="mailto:sagarathinarapu152@gmail.com" className="text-black dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors font-bold">
                    sagarathinarapu152@gmail.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-indigo-100 dark:bg-indigo-900/50 p-3 rounded-lg mr-4">
                  <FiPhone className="text-indigo-600 dark:text-indigo-400 text-xl" />
                </div>
                <div>
                  <h4 className="font-bold text-black dark:text-white">Phone</h4>
                  <a href="tel:+19133032247" className="text-black dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors font-bold">
                    +1 913-303-2247
                  </a>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-indigo-100 dark:bg-indigo-900/50 p-3 rounded-lg mr-4">
                  <FiMapPin className="text-indigo-600 dark:text-indigo-400 text-xl" />
                </div>
                <div>
                  <h4 className="font-bold text-black dark:text-white">Location</h4>
                  <p className="text-black dark:text-gray-400 font-bold">Denton, Texas</p>
                </div>
              </div>
              
              <div className="mt-8 bg-indigo-50 dark:bg-indigo-900/10 p-6 rounded-xl">
                <h4 className="font-bold text-lg text-black dark:text-white mb-3">Direct Contact</h4>
                <p className="text-black dark:text-gray-400 font-bold">
                  Prefer direct communication? Feel free to email or call me directly. I typically respond within 24 hours.
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                    Name {formErrors.name && <span className="text-red-500">*</span>}
                  </label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border ${
                      formErrors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                    } rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white font-medium`}
                    placeholder="Your name"
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-500 font-bold">{formErrors.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                    Email {formErrors.email && <span className="text-red-500">*</span>}
                  </label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border ${
                      formErrors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                    } rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white font-medium`}
                    placeholder="your.email@example.com"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-500 font-bold">{formErrors.email}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                  Subject {formErrors.subject && <span className="text-red-500">*</span>}
                </label>
                <input 
                  type="text" 
                  id="subject" 
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border ${
                    formErrors.subject ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white font-medium`}
                  placeholder="Subject"
                />
                {formErrors.subject && (
                  <p className="mt-1 text-sm text-red-500 font-bold">{formErrors.subject}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                  Message {formErrors.message && <span className="text-red-500">*</span>}
                </label>
                <textarea 
                  id="message" 
                  name="message"
                  rows="5" 
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border ${
                    formErrors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white font-medium`}
                  placeholder="Your message"
                ></textarea>
                {formErrors.message && (
                  <p className="mt-1 text-sm text-red-500 font-bold">{formErrors.message}</p>
                )}
              </div>
              
              <button 
                type="submit" 
                disabled={formStatus.submitting}
                className={`px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-500/10 flex items-center justify-center font-bold ${
                  formStatus.submitting ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {formStatus.submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
            
            <div className="mt-8 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-sm text-black dark:text-gray-400 font-bold">
              <p>Your information is secure. I'll only use it to respond to your inquiry.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;